import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { api, ApiError } from '@/api/client';
import { telegramInitData } from '@/services/telegram';

type AuthState = 'loading' | 'authenticated' | 'onboarding' | 'outside_telegram' | 'error';

export const useAuthStore = defineStore('auth', () => {
  const state = ref<AuthState>('loading');
  const error = ref('');
  const errorDetails = ref('');
  const demo = ref(false);
  const isReady = computed(() => state.value !== 'loading');

  async function initialize(): Promise<void> {
    state.value = 'loading';
    error.value = '';
    errorDetails.value = '';
    const initData = telegramInitData();
    const demoAllowed = import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEMO === 'true' && new URLSearchParams(location.search).has('demo');
    if (!initData && demoAllowed) { demo.value = true; state.value = 'authenticated'; return; }
    try {
      if (initData) {
        const result = await api<{ onboardingComplete: boolean }>('/auth/telegram', { method: 'POST', body: JSON.stringify({ initData, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }) });
        state.value = result.onboardingComplete ? 'authenticated' : 'onboarding';
      } else {
        const result = await api<{ authenticated: boolean; onboardingComplete: boolean }>('/auth/session');
        state.value = result.onboardingComplete ? 'authenticated' : 'onboarding';
      }
    } catch (reason) {
      if (!initData && reason instanceof ApiError && reason.status === 401) state.value = 'outside_telegram';
      else {
        state.value = 'error';
        error.value = reason instanceof Error ? reason.message : 'Не удалось войти';
        if (reason instanceof ApiError) {
          errorDetails.value = [reason.code, reason.status ? `HTTP ${reason.status}` : '', reason.requestId ? `ID ${reason.requestId}` : ''].filter(Boolean).join(' · ');
        }
      }
    }
  }

  async function finishOnboarding(): Promise<void> {
    await api('/auth/onboarding', { method: 'POST', body: JSON.stringify({ ageConfirmed16Plus: true, termsVersion: '1.0', privacyVersion: '1.0', aiProcessingVersion: '1.0' }) });
    state.value = 'authenticated';
  }

  return { state, error, errorDetails, demo, isReady, initialize, finishOnboarding };
});
